import React, { useRef } from "react";
import { Field, FieldArray } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { useAuth } from "@peracto/peracto-auth";

import { IBTSequenceRow } from "./IBTSequenceRow";
import { createEmptySequence, formatDaysOfWeek } from "./constants";
import { canEditIbtConfig } from "../../util";

const SEQUENCE_INDEX_PATTERN = /^ibtConfig\[(\d+)\]/;

const ReadOnlyIBTConfig = ({ sequences }) =>
    sequences.length > 0 ? (
        <table className="table table-sm mb-1 align-top">
            <colgroup>
                <col width="30%" />
                <col width="70%" />
            </colgroup>

            <tbody>
                {sequences.map((item, index) => (
                    <tr key={index} data-id={index} data-testid={`ibt-sequence-${index}`}>
                        <td className="align-top">
                            <h5 className="mb-2 font-weight-bold">
                                Sequence {index + 1}
                            </h5>
                            <p>
                                Time Range Start:{" "}
                                <span className="font-bold">
                                    {item.timeRangeStart}
                                </span>
                            </p>
                            <p>
                                Time Range Stop:{" "}
                                <span className="font-bold">
                                    {item.timeRangeEnd}
                                </span>
                            </p>
                            <p>
                                Days of Week:{" "}
                                <span className="font-bold">
                                    {formatDaysOfWeek(item.daysOfWeek)}
                                </span>
                            </p>
                        </td>
                        <td className="align-top">
                            {Array.isArray(item.branchSequence)
                                ? item.branchSequence.join(" → ")
                                : ""}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    ) : (
        <p className="py-8 mb-0 text-center">
            There are no IBT config sequences set up for this location.
        </p>
    );

const IBTConfigErrorSummary = ({ violations, onNavigate }) => (
    <div
        className="alert alert-danger"
        role="alert"
        data-testid="ibt-config-error-summary"
    >
        <p className="mb-2 font-weight-bold">
            There {violations.length === 1 ? "is a problem" : "are problems"}{" "}
            with the IBT config below:
        </p>
        <ul className="mb-0 pl-3">
            {violations.map((violation, i) => {
                const match = SEQUENCE_INDEX_PATTERN.exec(
                    violation.propertyPath
                );
                const sequenceIndex = match ? parseInt(match[1], 10) : null;

                return (
                    <li key={i}>
                        {sequenceIndex !== null ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-link p-0 align-baseline"
                                    style={{ userSelect: "text" }}
                                    data-testid={`ibt-config-error-goto-${sequenceIndex}`}
                                    onClick={() => onNavigate(sequenceIndex)}
                                >
                                    Sequence {sequenceIndex + 1}
                                </button>
                                : {violation.message}
                            </>
                        ) : (
                            violation.message
                        )}
                    </li>
                );
            })}
        </ul>
    </div>
);

export const LocationIBTConfig = () => {
    const { user } = useAuth();
    const canEditIbt = canEditIbtConfig(user);
    const sequenceRefs = useRef({});

    const scrollToSequence = (index) => {
        const node = sequenceRefs.current[index];
        if (node?.scrollIntoView) {
            node.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = node.querySelector("input");
            if (input) {
                input.focus();
            }
        }
    };

    return (
        <Field name="ibtConfig">
            {({ field, form }) => {
                if (!canEditIbt) {
                    return (
                        <ReadOnlyIBTConfig sequences={field.value || []} />
                    );
                }

                const ibtConfigViolations =
                    form.status?.ibtConfigViolations || [];

                return (
                    <FieldArray name={field.name}>
                        {({ push, remove }) => (
                            <div
                                className="form-group"
                                data-testid="ibt-config"
                            >
                                {ibtConfigViolations.length > 0 && (
                                    <IBTConfigErrorSummary
                                        violations={ibtConfigViolations}
                                        onNavigate={scrollToSequence}
                                    />
                                )}

                                {field.value?.length > 0 ? (
                                    field.value.map((_, index) => (
                                        <React.Fragment key={index}>
                                            {index > 0 && (
                                                <hr
                                                    className="my-3"
                                                    style={{
                                                        borderTop:
                                                            "1px solid #f0f0f0",
                                                    }}
                                                />
                                            )}
                                            <IBTSequenceRow
                                                index={index}
                                                name={`${field.name}[${index}]`}
                                                onRemove={() => remove(index)}
                                                innerRef={(el) =>
                                                    (sequenceRefs.current[
                                                        index
                                                    ] = el)
                                                }
                                            />
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <p className="py-8 mb-0 text-center">
                                        There are no IBT config sequences set
                                        up for this location.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary mt-3"
                                    data-testid="ibt-config-add-sequence"
                                    onClick={() =>
                                        push(createEmptySequence())
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        className="mr-1"
                                    />
                                    Add Sequence
                                </button>
                            </div>
                        )}
                    </FieldArray>
                );
            }}
        </Field>
    );
};
